// src/pages/TermsOfUse.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "../../mainpagecomponents/Header";
import {
    Box,
    Container,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Chip,
    Stack,
    Link,
    IconButton,
    useMediaQuery,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Icons
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import RuleFolderOutlinedIcon from "@mui/icons-material/RuleFolderOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import Footer from "../../mainpagecomponents/Footer";

const Bullet = ({ children }) => (
    <ListItem sx={{ alignItems: "flex-start", pl: 0 }}>
        <ListItemIcon sx={{ minWidth: 32, pt: "4px" }}>
            <CheckCircleOutlineIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
            primaryTypographyProps={{ sx: { lineHeight: 1.7, fontSize: 15 } }}
            primary={children}
        />
    </ListItem>
);

export default function TermsOfUse({ embed = false }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const surface = theme.palette.mode === "dark" ? "background.paper" : "#FDFCF7";

    const sections = useMemo(
        () => [
            { id: "acceptance", title: "1. Acceptance of Terms", icon: <InfoOutlinedIcon /> },
            { id: "eligibility", title: "2. Eligibility", icon: <PersonAddAlt1OutlinedIcon /> },
            { id: "account", title: "3. Account Registration & Security", icon: <VerifiedUserOutlinedIcon /> },
            { id: "responsibilities", title: "4. User Responsibilities", icon: <RuleFolderOutlinedIcon /> },
            { id: "student-data", title: "5. Student Data Handling", icon: <SchoolOutlinedIcon /> },
            { id: "ai-tools", title: "6. AI Tools & Restrictions", icon: <SmartToyOutlinedIcon /> },
            { id: "third-party", title: "7. Third‑Party Services", icon: <LanguageOutlinedIcon /> },
            { id: "ip", title: "8. Intellectual Property", icon: <Inventory2OutlinedIcon /> },
            { id: "payments", title: "9. Payment & Subscription Terms", icon: <CreditCardOutlinedIcon /> },
            { id: "prohibited", title: "10. Prohibited Uses", icon: <BlockOutlinedIcon /> },
            { id: "liability", title: "11. Limitation of Liability", icon: <GavelOutlinedIcon /> },
            { id: "termination", title: "12. Termination", icon: <ExitToAppOutlinedIcon /> },
            { id: "law", title: "13. Governing Law", icon: <GavelOutlinedIcon /> },
            { id: "contact", title: "14. Contact Information", icon: <EmailOutlinedIcon /> },
        ],
        []
    );

    const refs = useRef(Object.fromEntries(sections.map(s => [s.id, null])));
    const [activeId, setActiveId] = useState(sections[0].id);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible[0]?.target?.id) setActiveId(visible[0].target.id);
            },
            { rootMargin: "-20% 0px -70% 0px", threshold: [0, 0.25, 0.6, 1] }
        );
        sections.forEach(({ id }) => {
            const node = refs.current[id];
            if (node) observer.observe(node);
        });
        return () => observer.disconnect();
    }, [sections]);

    const scrollTo = (id) => {
        const el = refs.current[id];
        if (!el) return;


        // In embedded modal, avoid scrolling the whole window.
        if (!embed) {
            window.scrollTo({
                top: el.offsetTop - 88,
                behavior: "smooth",
            });
        }
    };

    const LinkStyled = (props) => (
        <Link
            {...props}
            underline="hover"
            sx={{
                color: "secondary.main",
                fontWeight: 600,
                outlineOffset: "2px",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                "&:focus-visible": {
                    outline: `2px solid ${theme.palette.secondary.main}`,
                    borderRadius: 1,
                },
            }}
        />
    );

    const DateBar = (
        <Box
            sx={{
                backgroundColor: surface,
                pt: 16,
                pb: 6,
                borderBottom: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Container maxWidth="lg" sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                <Chip size="small" color="primary" variant="outlined" icon={<ListAltOutlinedIcon />} label="Terms of Service" />
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    <strong>Effective:</strong> Aug 7, 2025
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>•</Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    <strong>Last Updated:</strong> Aug 7, 2025
                </Typography>
            </Container>
        </Box>
    );

    const SectionWrap = ({ id, icon, title, children }) => {
        if (isMobile) {
            return (
                <Accordion
                    defaultExpanded={id === "acceptance"}
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        overflow: "hidden",
                        bgcolor: theme.palette.background.paper,
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {icon}
                            <Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box id={id} ref={(n) => (refs.current[id] = n)}>{children}</Box>
                    </AccordionDetails>
                </Accordion>
            );
        }

        return (
            <Paper
                elevation={0}
                id={id}
                ref={(n) => (refs.current[id] = n)}
                sx={{
                    p: { xs: 2, md: 3 },
                    mb: 3,
                    borderRadius: 3,
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    {icon}
                    <Typography variant="h6" fontWeight={800}>{title}</Typography>
                </Stack>
                {children}
            </Paper>
        );
    };

    return (
        <>

            {!embed && <Header />}



            <main>
                {!embed && DateBar}

                {DateBar}

                <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 3, md: 4 }}>
                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>
                                Terms of Service
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                Please read these Terms carefully. By using Gradely, you agree to them and to the Privacy Policy.
                            </Typography>

                            {/* Sections */}
                            <SectionWrap id="acceptance" icon={<InfoOutlinedIcon />} title="1. Acceptance of Terms">
                                <Typography sx={{ mb: 2 }}>
                                    Welcome to <strong>Gradely.ca</strong> (“Gradely”), operated by Gradely Inc. By creating an account, logging in,
                                    or using any Services, you agree to these Terms and our Privacy Policy. If you do not agree, do not use the Services.
                                    We may update these Terms from time to time. Continued use after posting changes means you accept the updated Terms.
                                </Typography>
                            </SectionWrap>

                            <SectionWrap id="eligibility" icon={<PersonAddAlt1OutlinedIcon />} title="2. Eligibility">
                                <List dense sx={{ mb: 2 }}>
                                    <Bullet>Only teachers, school administrators, or authorized educational personnel may register for an account.</Bullet>
                                    <Bullet>Students cannot create accounts and may only access reports or materials shared by teachers.</Bullet>
                                    <Bullet>You must be at least 19 years old (or the age of majority in your jurisdiction) to register.</Bullet>
                                </List>
                            </SectionWrap>

                            <SectionWrap id="account" icon={<VerifiedUserOutlinedIcon />} title="3. Account Registration & Security">
                                <List dense sx={{ mb: 2 }}>
                                    <Bullet>Provide accurate and complete registration information.</Bullet>
                                    <Bullet>Maintain the confidentiality of your login credentials.</Bullet>
                                    <Bullet>Notify us promptly of any unauthorized access.</Bullet>
                                    <Bullet>Do not share your account with others.</Bullet>
                                </List>
                            </SectionWrap>

                            <SectionWrap id="responsibilities" icon={<RuleFolderOutlinedIcon />} title="4. User Responsibilities">
                                <List dense sx={{ mb: 2 }}>
                                    <Bullet>Obtain verifiable parental/guardian consent before entering student personal information, where required by law.</Bullet>
                                    <Bullet>Ensure all data you enter is lawful, accurate, and appropriate for an educational platform.</Bullet>
                                    <Bullet>Do not upload content that is harassing, discriminatory, obscene, or otherwise unlawful.</Bullet>
                                </List>
                            </SectionWrap>

                            <SectionWrap id="student-data" icon={<SchoolOutlinedIcon />} title="5. Student Data Handling">
                                <List dense sx={{ mb: 2 }}>
                                    <Bullet>Gradely acts as a data processor; we do not collect student data directly from students or parents.</Bullet>
                                    <Bullet>All student data is stored exclusively on Microsoft Azure servers located in Canada.</Bullet>
                                    <Bullet>We comply with PIPEDA and, where applicable, PIPA (British Columbia).</Bullet>
                                    <Bullet>You may delete student data at any time. Deletion is permanent and irreversible.</Bullet>
                                    <Bullet>We never sell or use student data for advertising.</Bullet>
                                </List>
                            </SectionWrap>

                            <SectionWrap id="ai-tools" icon={<SmartToyOutlinedIcon />} title="6. AI Tools & Restrictions">
                                <List dense sx={{ mb: 2 }}>
                                    <Bullet>Do NOT enter student names or other PII into AI prompts.</Bullet>
                                    <Bullet>
                                        AI tools are powered by OpenAI and subject to OpenAI’s Privacy Policy:&nbsp;
                                        <LinkStyled href="https://openai.com/privacy" target="_blank" rel="noreferrer">
                                            openai.com/privacy <OpenInNewRoundedIcon fontSize="small" />
                                        </LinkStyled>
                                    </Bullet>
                                    <Bullet>Any identifiable data entered into AI tools is at your own risk and not covered by Gradely’s internal safeguards.</Bullet>
                                    <Bullet>Gradely does not monitor, retain, or control the content submitted to AI tools.</Bullet>
                                    <Bullet>We are not responsible for the accuracy or suitability of AI‑generated content.</Bullet>
                                </List>
                            </SectionWrap>

                            <SectionWrap id="third-party" icon={<LanguageOutlinedIcon />} title="7. Third‑Party Services">
                                <Typography sx={{ mb: 2 }}>
                                    Gradely integrates with Google & Microsoft (authentication) and OpenAI (AI features). Your use of these services is
                                    subject to their own terms and privacy policies.
                                </Typography>
                            </SectionWrap>

                            <SectionWrap id="ip" icon={<Inventory2OutlinedIcon />} title="8. Intellectual Property">
                                <Typography sx={{ mb: 2 }}>
                                    All Gradely software, content, and branding are the property of Gradely Inc. We grant a limited, non‑exclusive,
                                    non‑transferable license for educational use. AI‑generated content is licensed for educational use only and may not be
                                    resold or distributed commercially without permission.
                                </Typography>
                            </SectionWrap>

                            <SectionWrap id="payments" icon={<CreditCardOutlinedIcon />} title="9. Payment & Subscription Terms (if applicable)">
                                <List dense sx={{ mb: 2 }}>
                                    <Bullet>Free accounts have limited access to certain AI and platform features.</Bullet>
                                    <Bullet>Paid subscriptions renew automatically unless canceled before renewal.</Bullet>
                                    <Bullet>Fees are non‑refundable except where required by law.</Bullet>
                                    <Bullet>We may change pricing with reasonable notice.</Bullet>
                                </List>
                            </SectionWrap>

                            <SectionWrap id="prohibited" icon={<BlockOutlinedIcon />} title="10. Prohibited Uses">
                                <List dense sx={{ mb: 2 }}>
                                    <Bullet>No unauthorized access attempts, reverse engineering, or tampering.</Bullet>
                                    <Bullet>Do not upload malicious code or files.</Bullet>
                                    <Bullet>Do not share AI‑generated outputs that contain prohibited or harmful content.</Bullet>
                                </List>
                            </SectionWrap>

                            <SectionWrap id="liability" icon={<GavelOutlinedIcon />} title="11. Limitation of Liability">
                                <Typography sx={{ mb: 2 }}>
                                    To the maximum extent allowed by law, Gradely is not liable for indirect, incidental, or consequential damages,
                                    including loss of data caused by user actions or deletions, misuse of AI tools, service interruptions, or actions of
                                    third‑party providers.
                                </Typography>
                            </SectionWrap>

                            <SectionWrap id="termination" icon={<ExitToAppOutlinedIcon />} title="12. Termination">
                                <Typography sx={{ mb: 2 }}>
                                    We may suspend or terminate your account for breaches of these Terms or suspicious activity. Upon termination, access
                                    ends immediately and data associated with your account may be permanently deleted after 30 days.
                                </Typography>
                            </SectionWrap>

                            <SectionWrap id="law" icon={<GavelOutlinedIcon />} title="13. Governing Law">
                                <Typography sx={{ mb: 2 }}>
                                    These Terms are governed by the laws of British Columbia, Canada. Disputes will be resolved in the courts of British Columbia.
                                </Typography>
                            </SectionWrap>

                            <SectionWrap id="contact" icon={<EmailOutlinedIcon />} title="14. Contact Information">
                                <Typography sx={{ mb: 1 }}>
                                    If you have questions about these Terms or your data, contact us at:&nbsp;
                                    <strong>info@gradely.ca</strong>
                                </Typography>
                            </SectionWrap>

                            {/* Back to top */}
                            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                                <IconButton
                                    aria-label="Back to top"
                                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                                    sx={{
                                        border: `1px solid ${theme.palette.divider}`,
                                        bgcolor: theme.palette.background.paper,
                                        ":hover": { bgcolor: theme.palette.action.hover },
                                    }}
                                >
                                    <ArrowUpwardRoundedIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Sticky TOC (desktop only) */}
                        {!isMobile && (
                            <Box
                                sx={{
                                    width: 280,
                                    position: "sticky",
                                    top: 96,
                                    alignSelf: "flex-start",
                                    height: "fit-content",
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        border: `1px solid ${theme.palette.divider}`,
                                        bgcolor: theme.palette.background.paper,
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                        <ListAltOutlinedIcon />
                                        <Typography variant="subtitle1" fontWeight={800}>On this page</Typography>
                                    </Stack>
                                    <Divider sx={{ mb: 1 }} />
                                    <List dense sx={{ py: 0 }}>
                                        {sections.map((s) => (
                                            <ListItem
                                                key={s.id}
                                                disableGutters
                                                sx={{
                                                    px: 0,
                                                    my: 0.25,
                                                    cursor: "pointer",
                                                    borderRadius: 1.5,
                                                    bgcolor: activeId === s.id ? theme.palette.action.selected : "transparent",
                                                    transition: "background-color .15s",
                                                    "&:hover": { bgcolor: theme.palette.action.hover },
                                                }}
                                                onClick={() => scrollTo(s.id)}
                                            >
                                                <ListItemIcon sx={{ minWidth: 32 }}>{s.icon}</ListItemIcon>
                                                <ListItemText
                                                    primaryTypographyProps={{
                                                        fontSize: 14,
                                                        fontWeight: activeId === s.id ? 700 : 500,
                                                    }}
                                                    primary={s.title}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </Box>
                        )}
                    </Stack>
                </Container>
            </main>
            {!embed && <Footer />}
        </>
    );
}
